import type { OpportunityRelatedExtraGuestSurcharge } from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

import { formatDateRange } from '~/utils/dateRangeUtils';

import type { SurchargeRoomAssignment } from '../../types';

import type { DealOptionGroup } from './dealOptionsModel';

export type RoomInfoTab = 'egs' | 'surcharges' | 'blackouts';

export type BlackoutEntry = {
  dateRange?: string;
  id: string;
  label: string;
};

export type SelectedRoomInfo = {
  blackouts: Array<BlackoutEntry>;
  egsRows: Array<OpportunityRelatedExtraGuestSurcharge>;
  group: DealOptionGroup;
  surcharges: Array<SalesforceSurchargePeriod>;
};

type DisplayBlackoutEntry = {
  dateRange?: string;
  name: string;
};

type MarginCheckStatus = 'red' | 'amber' | 'green';

function getMarginCheckStatus(value: string | null | undefined): MarginCheckStatus | undefined {
  const normalizedValue = value?.toLowerCase();

  if (!normalizedValue) {
    return undefined;
  }

  if (normalizedValue.includes('reddot') || normalizedValue.includes('alt="red"')) {
    return 'red';
  }

  if (normalizedValue.includes('amberdot') || normalizedValue.includes('alt="amber"')) {
    return 'amber';
  }

  if (normalizedValue.includes('greendot') || normalizedValue.includes('alt="green"')) {
    return 'green';
  }

  return undefined;
}

export function getMarginCheckColor(value: string | null | undefined) {
  const marginCheckStatus = getMarginCheckStatus(value);

  if (marginCheckStatus === 'green') {
    return 'success.main';
  }

  if (marginCheckStatus === 'amber') {
    return 'warning.main';
  }

  if (marginCheckStatus === 'red') {
    return 'error.main';
  }

  return 'text.secondary';
}

export function getDisplayBlackoutEntry(entry: BlackoutEntry): DisplayBlackoutEntry {
  if (entry.dateRange) {
    return {
      dateRange: entry.dateRange,
      name: entry.label,
    };
  }

  const labelMatch = entry.label.match(/^(?<name>.+?)\s*\((?<dateRange>[^)]+)\)$/);

  if (!labelMatch?.groups) {
    return { name: entry.label };
  }

  return {
    dateRange: labelMatch.groups.dateRange,
    name: labelMatch.groups.name,
  };
}

export function getRoomInfoCount({
  blackouts,
  egsRows,
  surcharges,
}: {
  blackouts: Array<BlackoutEntry>;
  egsRows: Array<OpportunityRelatedExtraGuestSurcharge>;
  surcharges: Array<SalesforceSurchargePeriod>;
}) {
  return egsRows.length + surcharges.length + blackouts.length;
}

export function getGroupRoomRateIds(group: DealOptionGroup) {
  return new Set(group.deals.flatMap((deal) => deal.roomRateIds));
}

export function getScopedSurcharges({
  appliedRoomsBySurchargeId,
  roomRateIds,
  surcharges,
}: {
  appliedRoomsBySurchargeId: Record<string, Array<SurchargeRoomAssignment>>;
  roomRateIds: Set<string>;
  surcharges: Array<SalesforceSurchargePeriod>;
}) {
  if (roomRateIds.size === 0) {
    return [];
  }

  return surcharges.filter((surcharge) =>
    appliedRoomsBySurchargeId[surcharge.id]?.some((room) => roomRateIds.has(room.roomRateId)),
  );
}

export function getScopedEgsRows({
  egsByRoomRateId,
  roomRateIds,
}: {
  egsByRoomRateId: Record<string, Array<App.ExtraGuestSurcharges>>;
  roomRateIds: Set<string>;
}): Array<OpportunityRelatedExtraGuestSurcharge> {
  return Array.from(roomRateIds).flatMap((roomRateId) =>
    (egsByRoomRateId[roomRateId] ?? []).flatMap((egs) => getEgsRows(roomRateId, egs)),
  );
}

export function getScopedBlackoutEntries({
  blackoutsByRoomRateId,
  roomRateIds,
}: {
  blackoutsByRoomRateId: Record<string, Array<App.BlackoutDate>>;
  roomRateIds: Set<string>;
}): Array<BlackoutEntry> {
  const entries = new Map<string, BlackoutEntry>();

  Array.from(roomRateIds).forEach((roomRateId) => {
    (blackoutsByRoomRateId[roomRateId] ?? []).forEach((blackout) => {
      const dateRange = formatDateRange(blackout.start_date, blackout.end_date);
      const key = [
        blackout.start_date,
        blackout.end_date,
        blackout.monday,
        blackout.tuesday,
        blackout.wednesday,
        blackout.thursday,
        blackout.friday,
        blackout.saturday,
        blackout.sunday,
        blackout.restriction_type,
      ].join(':');

      entries.set(key, {
        dateRange,
        id: blackout.id,
        label: getBlackoutLabel(blackout),
      });
    });
  });

  return Array.from(entries.values());
}

function getBlackoutLabel(blackout: App.BlackoutDate) {
  if (blackout.restriction_type === 'arrival') {
    return 'Check-in blackout';
  }

  if (blackout.restriction_type === 'departure') {
    return 'Check-out blackout';
  }

  return 'Blackout dates';
}

function getEgsRows(roomRateId: string, egs: App.ExtraGuestSurcharges): Array<OpportunityRelatedExtraGuestSurcharge> {
  return [
    getEgsRow(roomRateId, egs, 'Adult', 'adult'),
    getEgsRow(roomRateId, egs, 'Child', 'child'),
    getEgsRow(roomRateId, egs, 'Infant', 'infant'),
    getEgsRow(roomRateId, egs, 'Teenager', 'teenager'),
  ].filter((egsRow) => Number(egsRow.cost_price__c) > 0 || Number(egsRow.sell_price_aud__c) > 0);
}

function getEgsRow(
  roomRateId: string,
  egs: App.ExtraGuestSurcharges,
  label: string,
  fieldPrefix: 'adult' | 'child' | 'infant' | 'teenager',
): OpportunityRelatedExtraGuestSurcharge {
  return {
    id: `${roomRateId}:${egs.id ?? 'egs'}:${fieldPrefix}`,
    cost_price__c: egs[`${fieldPrefix}_cost`],
    currency: egs.currency,
    extra_guest_type__c: label,
    sell_price_aud__c: egs[`${fieldPrefix}_amount`],
  };
}
